import { useEffect, useRef } from "react";
import { Modal } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFocusOnOpen } from "../hooks/useFocusOnOpen";
import { useViewport } from "../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../utils/modalStyles";

// Над всеки модал/чекмедже на страницата (antd ги държи около 1000), под
// toast-овете на `message` (2010).
const MODAL_Z_INDEX = 2000;

const reloadApp = () => window.location.reload();

/**
 * „Налична е нова версия" — центриран модал БЕЗ затваряне (нито X, нито фон,
 * нито Esc): единственият изход е „Презареди". Показва го
 * `VersionCheckNotifier` чак когато новият билд е готов за зареждане.
 *
 * Кухненските монитори имат само клавиатура: фокусът е на бутона, а Enter
 * презарежда, където и да е фокусът. Докато модалът е отворен, НИТО ЕДИН
 * клавиш не стига до екрана отдолу — иначе „+" на монитора би маркирал
 * продукт, а Esc би върнал назад през `SharedAccountGate`.
 */
export default function NewVersionModal({ open }: { open: boolean }) {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const reloadButtonRef = useRef<HTMLButtonElement>(null);
  useFocusOnOpen(open, reloadButtonRef);

  useEffect(() => {
    if (!open) return;
    // Capture на window — минава преди всички останали keydown handler-и
    // на страницата (те са на window в bubble фазата) и ги спира.
    const onKeyDown = (e: KeyboardEvent) => {
      e.stopImmediatePropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        reloadApp();
        return;
      }
      // Tab би изкарал фокуса от модала (focus trap-ът на antd е React
      // handler и вече не стига до него), Esc — правилото за custom Esc.
      // Останалите клавиши пазят поведението на браузъра (F5, Ctrl+R).
      if (e.key === "Tab" || e.key === "Escape") e.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  return (
    <Modal
      open={open}
      footer={null}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      zIndex={MODAL_Z_INDEX}
      width={isMobile ? undefined : isTablet ? 420 : 460}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="bg-surface">
        <div
          className={`flex items-start gap-4 border-b border-shade-2 ${
            isNarrow ? "px-4 py-[14px]" : "px-6 py-5"
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] bg-brand-tint">
            <SyncOutlined className="!text-[22px] !text-brand-deep-ink" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {t("common.newVersionTitle")}
            </h2>
            <p className="mb-0 mt-1 text-[13.5px] font-medium leading-[1.4] text-body">
              {t("common.newVersionMessage")}
            </p>
          </div>
        </div>
        <div className={isNarrow ? "p-4" : "px-6 py-5"}>
          <button
            ref={reloadButtonRef}
            type="button"
            onClick={reloadApp}
            className="relative flex h-12 w-full cursor-pointer items-center justify-center rounded-[10px] border-none bg-brand text-[15px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep focus:outline-none focus:ring-4 focus:ring-brand/30"
          >
            {t("common.newVersionReload")}
            {/* Клавишът горе-дясно, както бутоните в „Маркиране" (KbdChip в
                ordering/components/modalPrimitives.tsx), но по-едър и по-плътен:
                тук е единственото действие, а на монитора се чете отдалеч. */}
            <span className="pointer-events-none absolute right-3 top-1 font-pos-mono text-[20px] font-semibold leading-none text-white/85">
              ⏎
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
